import { useCallback, useContext, useEffect } from 'react';
import { getUser2LetterLanguageCode } from '@Utils/getUserLanguage';
import { useExchangeRates } from '@Global/apollo/query/getExchangeRates/useExchangeRates';
import { Currency, Money, StakeValueClass, WalletToken } from '@Global/apollo/types';
import { AppContext, ECurrenciesPair, Pairs, TAppContext } from '@Global/store/appContext';
import { EIconType } from '@Global/ui/icon/iconsMap';

export type TTotalBalanceUSD = {
  includes: Currency[];
  excludes: Currency[];
  total: number;
};

const EXCHANGE_RATES_REFETCH_TIMEOUT = 60000;

export const useUpdateLocalTONToCurrenciesRate = () => {
  const { setRates } = useContext(AppContext);
  const { fetchExchangeRates } = useExchangeRates();

  const updateRates = useCallback(
    () =>
      fetchExchangeRates().then((res) => {
        const rates = res?.data?.exchangeRates || [];
        setRates(rates);
      }),
    [fetchExchangeRates, setRates],
  );

  useEffect(() => {
    updateRates();
    const interval = setInterval(() => {
      try {
        updateRates();
      } catch (e) {
        console.log(e);
      }
    }, EXCHANGE_RATES_REFETCH_TIMEOUT);
    return () => {
      clearInterval(interval);
    };
  }, []);
};

export const usdToTon = (tonValue: number, rate: number): number => {
  return parseFloat((tonValue * rate).toFixed(1));
};

/**
 * Конвертирует сумму из одной цвалюты в другую с использованием курсов обмена
 *
 * @param money - Объект Money, содержащий сумму и исходную валюту
 * @param currency - Целевая валюта, в которую нужно конвертировать
 * @param currencyRates - Объект с курсами обмена валют
 * @returns Сумма в целевой валюте или undefined, если конвертация невозможна
 */
export const moneyToAmountInCurrency = (
  money: Money,
  currency: Currency,
  currencyRates: TAppContext['currencyRates'],
): number | undefined => {
  // Если валюты совпадают, возвращаем исходную сумму без конвертации
  if (money.currency === currency) {
    return money.amount;
  }

  // Формируем строку пары валют для поиска в объекте курсов
  const pair = `${money.currency}_${currency}`;

  // Если курс для данной пары валют существует, выполняем конвертацию
  if (currencyRates[pair as Pairs]) {
    // Умножаем сумму на курс и округляем до 2 знаков после запятой
    return Math.floor(money.amount * currencyRates[pair as Pairs] * 100) / 100;
  }

  // Если курс не найден, возвращаем undefined
  return undefined;
};

export const getTotalBalanceUSD = (
  tokens: WalletToken[],
  currencyRates: TAppContext['currencyRates'],
): TTotalBalanceUSD => {
  const includes: Currency[] = [];
  const excludes: Currency[] = [];
  let total = 0;

  for (const { balance } of tokens) {
    const amount = moneyToAmountInCurrency(balance, Currency.Usd, currencyRates);
    if (amount) {
      includes.push(balance.currency);
      total += amount;
    } else {
      excludes.push(balance.currency);
    }
  }

  return { total, includes, excludes };
};

/**
 * Formats total balance amount with spaces for thousands and comma for decimals.
 * Rounds down at most to 2 decimal places. Adds a '$' sign in front.
 *
 * Examples:
 * 0          -> $0
 * 1          -> $1
 * 1.24       -> $1,24
 * 1.359      -> $1,35
 * 1000       -> $1 000
 * 2322000.23 -> $2 322 000,23
 *
 * @param amount number
 * @returns string
 */
export const formatTotalBalanceUSD = (amount: number) => {
  return '$' + formatNumberWithIntl(amount);
};

export const formatNumberWithSpaces = (amount: number, maxDecimals?: number) => {
  let [int, decimal] = amount.toString().split('.');
  int = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decimal && maxDecimals ? int + ',' + decimal.slice(0, maxDecimals ?? decimal.length) : int;
};

/**
 * Форматирует число с использованием Intl.NumberFormat, позволяя контролировать максимальное
 * количество десятичных знаков и используя локаль пользователя для форматирования (включая разделители групп).
 * Для русской локали разделителем групп будет пробел.
 * Эта функция использует округление для десятичных знаков.
 *
 * @param amount Число для форматирования.
 * @param maxDecimals Максимальное количество десятичных знаков (неотрицательное число).
 * @param locale Строка локали (например, 'ru-RU', 'en-US'). По умолчанию используется результат getUser2LetterLanguageCode().
 * @returns Отформатированная строка числа.
 *
 * @example
 * formatNumberWithIntl(12345.6789, 2, 'ru-RU') // => "12 345,68"
 * formatNumberWithIntl(12345, 2, 'ru-RU')       // => "12 345"
 * formatNumberWithIntl(12345.6, 2, 'ru-RU')    // => "12 345,6"
 */
export const formatNumberWithIntl = (amount: number, maxDecimals?: number, locale?: string): string => {
  const loc = locale || getUser2LetterLanguageCode();
  const options: Intl.NumberFormatOptions = {
    useGrouping: true, // Обеспечивает разделение групп (например, пробелами для 'ru')
  };

  if (typeof maxDecimals === 'number' && maxDecimals >= 0) {
    options.maximumFractionDigits = maxDecimals;
    // Примечание: Intl.NumberFormat будет округлять, а не обрезать.
    // Поведение существующей функции formatNumberWithSpaces (которая не дополняет нулями, если их меньше maxDecimals)
    // соответствует поведению Intl.NumberFormat при указании только maximumFractionDigits.
    // Например, formatNumberWithIntl(123.4, 2) -> "123,4" (для 'ru'), а не "123,40".
    // Если требуется строгое количество знаков (с дополнением нулей),
    // необходимо также установить options.minimumFractionDigits = maxDecimals.
  }

  const formatter = new Intl.NumberFormat(loc, options);
  return formatter.format(amount);
};

export const round = (amount: number, placesMultiplier = 1e9) => {
  return Math.floor(amount * placesMultiplier) / placesMultiplier;
};

export const getTokenIconType = (currency: Currency): EIconType => {
  switch (currency) {
    case Currency.Ton:
      return EIconType.TON_TOKEN;
    case Currency.Fntf:
      return EIconType.FT_TOKEN;
    case Currency.Fton:
      return EIconType.FTON;
    case Currency.Xtr:
      return EIconType.XTR;
  }

  return EIconType.USDT;
};

// TODO: Merge min and currency into Money object
export type TStakeClass = {
  min: number;
  currency: Currency;
  name: StakeValueClass;
  options: TStakeClassOption[];
};

export type TStakeClassOption = {
  days: number;
  apy: number;
};

// Should be sorted by `min` in decreasing order for helper functions to work properly
// export const stakeClasses: TStakeClass[] = [
//   {
//     min: 3_000_000,
//     currency: Currency.Fton,
//     name: StakeValueClass.Platinum,
//     options: [
//       { days: 90, apy: 130 },
//       { days: 180, apy: 140 },
//     ],
//   },
//   {
//     min: 1_000_000,
//     currency: Currency.Fton,
//     name: StakeValueClass.Gold,
//     options: [
//       { days: 90, apy: 110 },
//       { days: 180, apy: 120 },
//     ],
//   },
//   {
//     min: 300_000,
//     currency: Currency.Fton,
//     name: StakeValueClass.Silver,
//     options: [
//       { days: 90, apy: 100 },
//       { days: 180, apy: 110 },
//     ],
//   },
//   {
//     min: 100_000,
//     currency: Currency.Fton,
//     name: StakeValueClass.Bronze,
//     options: [
//       { days: 90, apy: 90 },
//       { days: 180, apy: 100 },
//     ],
//   },
// ];

/**
 * Ежедневный_доход = (Сумма_стейкинга * (1+APY)^(Длительность_стейкинга / 365) - Сумма_стейкинга) / Длительность_стейкинга
 * @param deposit Сумма_стейкинга
 * @param apy APY 110% -> 1.1, 150% -> 1.5
 * @param duration Длительность_стейкинга
 * @returns Ежедневный_доход
 */
export const getStakeDailyAccruals = (deposit: number, apy: number, duration: number) => {
  return getStakeTotalIncome(deposit, apy, duration) / duration;
};

/**
 * Итоговый доход от введенной суммы = Сумма_стейкинга * (1+APY)^(Длительность_стейкинга / 365) - Сумма_стейкинга
 * @param deposit Сумма_стейкинга
 * @param apy APY 110% -> 1.1, 150% -> 1.5
 * @param duration Длительность_стейкинга
 * @returns Итоговый доход от введенной суммы
 */
export const getStakeTotalIncome = (deposit: number, apy: number, duration: number) => {
  return deposit * (1 + apy) ** (duration / 365) - deposit;
};

export const getStakeBackground = (stakeClass: StakeValueClass) => {
  switch (stakeClass) {
    case StakeValueClass.Platinum:
      return 'tw-bg-stake-platinum';
    case StakeValueClass.Gold:
      return 'tw-bg-stake-gold';
    case StakeValueClass.Silver:
      return 'tw-bg-stake-silver';
    case StakeValueClass.Bronze:
      return 'tw-bg-stake-bronze';
    default:
      return '';
  }
};

export const formatThousandsWithK = (amount: number, decimals = 9): string => {
  if (amount < 1000) {
    return amount.toFixed(decimals);
  }
  return (amount / 1000).toFixed(decimals) + 'K';
};

export const getExchangeRate = (from: Currency, to: Currency, currencyRates: TAppContext['currencyRates']): number => {
  const pair = (from + to) as ECurrenciesPair;
  if (currencyRates[pair]) {
    return currencyRates[pair];
  }
  const fromPair = (from + Currency.Usd) as ECurrenciesPair;
  const toPair = (to + Currency.Usd) as ECurrenciesPair;
  if (currencyRates[fromPair] && currencyRates[toPair]) {
    const fromUSD = currencyRates[fromPair];
    const toUSD = currencyRates[toPair];
    return fromUSD / toUSD;
  }
  return 0;
};

/**
 * Collection of percent fee values for each currency pair.
 * Key = Currency from + Currency to
 *
 * For example:
 * If fee for swapping TON to FTON is 3% then { TONFTON: 3 }
 */
export const swapFeeRates: { [key in ECurrenciesPair]?: number } = {
  TONFTON: 3,
};

export const getFeeRate = (fromCurrency: Currency, toCurrency: Currency) => {
  const pair = (fromCurrency + toCurrency) as ECurrenciesPair;
  const rate = swapFeeRates[pair] || 0;
  return 1 - rate / 100;
};

export const getExchangeFee = (amount: number, from: Currency, to: Currency): Money | undefined => {
  const pair = (from + to) as ECurrenciesPair;
  if (!swapFeeRates[pair]) {
    return;
  }
  return { amount: round(amount * (1 - getFeeRate(from, to))), currency: from };
};

export const dailyLimits: { [key in Currency]?: number } = {
  TON: 50,
};

export const createNumberFormatter = (options: Intl.NumberFormatOptions = {}) => {
  const loc = getUser2LetterLanguageCode();
  const formatter = new Intl.NumberFormat(loc, options);

  return (value: number) => {
    return formatter.format(value);
  };
};

export const formatPrice = createNumberFormatter({
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const formatPriceNoDecimalDigits = createNumberFormatter({
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
